import axios from 'axios';

const GET_CACHE_TTL = 10000;
const getCache = new Map();
const pendingGets = new Map();

const buildGetKey = (url, config = {}) => {
  const params = config.params ? JSON.stringify(config.params, Object.keys(config.params).sort()) : '';
  return `${url}?${params}`;
};

const clearGetCache = () => {
  getCache.clear();
  pendingGets.clear();
};

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:3001',
  timeout: 15000,
  withCredentials: true,
});

// Attach JWT token
api.interceptors.request.use((config) => {
  if ((config.method || 'get').toLowerCase() !== 'get') clearGetCache();

  if (config.data instanceof FormData) {
    config.headers?.delete?.('Content-Type');
    delete config.headers?.['Content-Type'];
    delete config.headers?.['content-type'];
  }

  try {
    const stored = localStorage.getItem('hakken-auth');
    if (stored) {
      const { state } = JSON.parse(stored);
      if (state?.token) config.headers.Authorization = `Bearer ${state.token}`;
    }
  } catch {}
  return config;
});

// Handle 401
api.interceptors.response.use(
  (res) => res,
  (err) => {
    const isAuthRequest = err.config?.url?.includes('/api/auth/login') || err.config?.url?.includes('/api/auth/register') || err.config?.url?.includes('/api/auth/me') || err.config?.url?.includes('/api/logista/register');
    const isPublicPage = ['/login', '/register', '/', '/logista/login', '/logista/register'].includes(window.location.pathname);

    if (err.response?.status === 401 && !isAuthRequest && !isPublicPage) {
      let redirectTo = '/login';
      try {
        const stored = localStorage.getItem('hakken-auth');
        if (stored) {
          const { state } = JSON.parse(stored);
          if (state?.user?.role === 'partner') redirectTo = '/logista/login';
        }
      } catch {}
      localStorage.removeItem('hakken-auth');
      window.location.href = redirectTo;
    }
    return Promise.reject(err);
  }
);

const rawGet = api.get.bind(api);

api.get = (url, config = {}) => {
  if (config.skipClientCache) return rawGet(url, config);

  const key = buildGetKey(url, config);
  const now = Date.now();
  const cached = getCache.get(key);

  if (cached && now - cached.timestamp < (config.clientCacheTtl || GET_CACHE_TTL)) {
    return Promise.resolve(cached.response);
  }

  if (pendingGets.has(key)) return pendingGets.get(key);

  const request = rawGet(url, config)
    .then((response) => {
      getCache.set(key, { timestamp: Date.now(), response });
      return response;
    })
    .finally(() => {
      pendingGets.delete(key);
    });

  pendingGets.set(key, request);
  return request;
};

export default api;
